import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import api from "../../services/api";
import { AcceptInviteResponse, MissingProfileField } from "../../types";
import { formatSSN, SSN_RULE } from "../estatelist/ssn";
import { formatTIN, TIN_RULE } from "../estatelist/tin";
import { US_STATES } from "../estatelist/usstates";

const { Title, Text, Paragraph } = Typography;

const Container = styled.div`
  display: flex;
  min-height: calc(100vh - 4rem);
  padding: 2rem;
  align-items: center;
  justify-content: center;
  background-color: #f4f6fb;
  flex-direction: column;
`;

const InviteSection = styled.div`
  width: 100%;
  max-width: 560px;
  padding: 2rem;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
`;

const Centered = styled.div`
  text-align: center;
`;

/** Which fields the form shows, and what each is called on screen. */
const FIELD_LABELS: Record<MissingProfileField, string> = {
  firstName: "First name",
  lastName: "Last name",
  phone: "Phone",
  dob: "Date of birth",
  taxId: "Social security number or taxpayer ID",
  addressLine1: "Address",
  city: "City",
  state: "State",
  zipCode: "Zip code",
  county: "County",
};

const ADDRESS_FIELDS: MissingProfileField[] = [
  "addressLine1",
  "city",
  "state",
  "zipCode",
  "county",
];

/**
 * The page an invited executor or attorney lands on.
 *
 * Accepting used to be the whole of it: the invite was recorded and the person
 * was handed a "Set Password" button with no explanation of what they had just
 * joined or what was still wanted from them. What the estate needs from
 * whoever signs a probate petition is a good deal more than an email address,
 * and this is often the only form either of them ever sees.
 *
 * So it accepts, then asks for exactly what is still blank, then sends them on
 * to set a password. Anything the person who invited them already filled in is
 * not asked for again, and where they filled in everything the form is skipped
 * entirely. Whatever the invitee enters here overwrites what the inviter said:
 * one was reporting on the other, and this is the person themselves.
 */
const AcceptInvite: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [accepted, setAccepted] = useState<AcceptInviteResponse | null>(null);
  const [detailsDone, setDetailsDone] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();

  const params = useParams();
  const token = params.token;

  useEffect(() => {
    const acceptInvite = async () => {
      try {
        const response = await api.post(`/user/invite/${token}`, { inviteToken: token });
        setAccepted(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to process invite.");
      } finally {
        setLoading(false);
      }
    };

    acceptInvite();
  }, [token]);

  const missing = accepted?.missingProfileFields ?? [];
  const asks = (field: MissingProfileField) => missing.includes(field);
  // The address lines are asked for together: somebody typing their street is
  // not helped by being asked for the town on a later screen.
  const asksAddress = ADDRESS_FIELDS.some(asks);

  const handleSaveDetails = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const address = {
        addressLine1: values.addressLine1 || undefined,
        city: values.city || undefined,
        state: values.state || undefined,
        zipCode: values.zipCode || undefined,
        county: values.county || undefined,
      };
      const hasAddress = Object.values(address).some(Boolean);

      await api.post(`/user/invite/${token}/profile`, {
        firstName: values.firstName || undefined,
        lastName: values.lastName || undefined,
        phone: values.phone || undefined,
        dob: values.dob ? values.dob.format("YYYY-MM-DD") : undefined,
        ssn: values.ssn || undefined,
        tin: values.tin || undefined,
        address: hasAddress ? address : undefined,
      });

      setDetailsDone(true);
      message.success("Thanks, your details are saved.");
    } catch (err: any) {
      if (err?.errorFields) return; // validation, already shown on the fields
      message.error(err.response?.data?.message || "Failed to save your details.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <InviteSection>
          <Centered>
            <Spin size="large" />
          </Centered>
        </InviteSection>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <InviteSection>
          <Alert message="Error" description={error} type="error" showIcon />
        </InviteSection>
      </Container>
    );
  }

  const setPassword = accepted?.passwordResetLink ? (
    <Button type="primary" size="large" block href={accepted.passwordResetLink}>
      {accepted.needsPassword ? "Set your password" : "Go to your account"}
    </Button>
  ) : null;

  // Everything the inviter filled in, or the invitee has just filled in.
  if (!missing.length || detailsDone) {
    return (
      <Container>
        <InviteSection>
          <Centered>
            <Title level={2}>Welcome to Wzee</Title>
            <Paragraph>
              {accepted?.needsPassword
                ? "Your invitation is accepted. Set a password and you are ready to sign in."
                : "Your invitation is accepted. Sign in with your existing password."}
            </Paragraph>
            {setPassword}
          </Centered>
        </InviteSection>
      </Container>
    );
  }

  return (
    <Container>
      <InviteSection>
        <Title level={2}>Welcome to Wzee</Title>
        <Paragraph>
          Your invitation is accepted. The court forms for this estate ask for a
          few details about you. Fill in what you can now, or skip and do it
          later from your account.
        </Paragraph>

        <Form form={form} layout="vertical">
          {(asks("firstName") || asks("lastName")) && (
            <Row gutter={16}>
              {asks("firstName") && (
                <Col span={12}>
                  <Form.Item label={FIELD_LABELS.firstName} name="firstName">
                    <Input placeholder="First name" />
                  </Form.Item>
                </Col>
              )}
              {asks("lastName") && (
                <Col span={12}>
                  <Form.Item label={FIELD_LABELS.lastName} name="lastName">
                    <Input placeholder="Last name" />
                  </Form.Item>
                </Col>
              )}
            </Row>
          )}

          {(asks("dob") || asks("phone")) && (
            <Row gutter={16}>
              {asks("dob") && (
                <Col span={12}>
                  <Form.Item label={FIELD_LABELS.dob} name="dob">
                    <DatePicker
                      style={{ width: "100%" }}
                      format="YYYY-MM-DD"
                      disabledDate={(current) => current && current > dayjs().endOf("day")}
                    />
                  </Form.Item>
                </Col>
              )}
              {asks("phone") && (
                <Col span={12}>
                  <Form.Item label={FIELD_LABELS.phone} name="phone">
                    <Input placeholder="Phone number" />
                  </Form.Item>
                </Col>
              )}
            </Row>
          )}

          {asks("taxId") && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Social security number"
                  name="ssn"
                  normalize={formatSSN}
                  rules={[SSN_RULE]}
                  extra="Stored encrypted. Used only on court documents."
                >
                  <Input placeholder="123-45-6789" maxLength={11} />
                </Form.Item>
              </Col>
              <Col span={12}>
                {/* An executor need not be a person: a bank's trust department
                    or a trust company has a TIN and no SSN at all. */}
                <Form.Item
                  label="Taxpayer ID (TIN)"
                  name="tin"
                  normalize={formatTIN}
                  rules={[TIN_RULE]}
                  extra="If you are acting as a firm or trust company."
                >
                  <Input placeholder="12-3456789" maxLength={10} />
                </Form.Item>
              </Col>
            </Row>
          )}

          {asksAddress && (
            <>
              <Form.Item label={FIELD_LABELS.addressLine1} name="addressLine1">
                <Input placeholder="Street address" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label={FIELD_LABELS.city} name="city">
                    <Input placeholder="City" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={FIELD_LABELS.state} name="state">
                    <Select allowClear showSearch placeholder="State" optionFilterProp="children">
                      {US_STATES.map((state) => (
                        <Select.Option key={state.code} value={state.code}>
                          {state.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={FIELD_LABELS.zipCode} name="zipCode">
                    <Input placeholder="Zip code" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label={FIELD_LABELS.county} name="county">
                <Input placeholder="County of residence" />
              </Form.Item>
            </>
          )}
        </Form>

        <Row gutter={16}>
          <Col span={12}>
            <Button block onClick={() => setDetailsDone(true)} disabled={saving}>
              Skip for now
            </Button>
          </Col>
          <Col span={12}>
            <Button type="primary" block loading={saving} onClick={handleSaveDetails}>
              Save and continue
            </Button>
          </Col>
        </Row>

        <Text type="secondary" style={{ marginTop: 16, textAlign: "center" }}>
          {accepted?.needsPassword
            ? "You will set your password next."
            : "Sign in with your existing password next."}
        </Text>
      </InviteSection>
    </Container>
  );
};

export default AcceptInvite;
